import * as ss from 'superstruct';
import { OutputStreamDestinationConfig } from '../types';
import { ConversionOutputStreamDestination } from './destination-list';
import { GoogleAdsDataManagerOutputStreamDestination } from './google-ads-data-manager-output-stream';
import { MetaAdsOutputStreamDestination } from './meta-ads-output-stream';
import { MicrosoftAdsConversionOutputStreamDestination } from './microsoft-ads-conversion-output-stream';
import { OpenAiAdsOutputStreamDestination } from './openai-ads-output-stream';

type AccountIdField = [field: string, struct: ss.Struct<string, null>];

/** The account-id field of each destination that has one. */
const destinationAccountIdFields: Partial<
  Record<ConversionOutputStreamDestination, AccountIdField>
> = {
  'google-ads-data-manager': [
    'customerId',
    GoogleAdsDataManagerOutputStreamDestination.schema.customerId,
  ],
  'meta-ads': ['accountId', MetaAdsOutputStreamDestination.schema.accountId],
  'microsoft-ads-conversion': [
    'customerId',
    MicrosoftAdsConversionOutputStreamDestination.schema.customerId,
  ],
};

/**
 * Destinations checked whole: their consumers reject an invalid config, which
 * would otherwise block every conversion after it's saved.
 */
const validatedDestinations: Partial<
  Record<ConversionOutputStreamDestination, ss.Struct<any, any>>
> = {
  'openai-ads': OpenAiAdsOutputStreamDestination,
};

/**
 * `destination` with its account id normalized, if its kind has one. Throws a
 * `StructError` with a user-facing message if that id is missing or invalid,
 * or if a destination in `validatedDestinations` is invalid anywhere.
 *
 * The data provider validates writes against the generic
 * `OutputStreamDestinationConfig`, so this is how account ids get normalized.
 */
export const normalizeOutputStreamDestination = (
  destination: OutputStreamDestinationConfig,
): OutputStreamDestinationConfig => {
  const name = destination.name as ConversionOutputStreamDestination;
  const wholeStruct = validatedDestinations[name];
  if (wholeStruct) {
    return ss.create(destination, wholeStruct) as OutputStreamDestinationConfig;
  }

  const accountIdField = destinationAccountIdFields[name];
  if (!accountIdField) {
    return destination;
  }

  const [field, struct] = accountIdField;
  return {
    ...destination,
    [field]: ss.create(destination[field] ?? '', struct),
  };
};
